// Tiny, dependency-free registry of in-progress edits that would be lost on a
// hard navigation (tab close, manual URL change, the idle-logout redirect).
//
// Dialogs register a unique key while they hold unsaved input and clear it when
// they close or save. AppShell reads `hasUnsaved()` from a `beforeunload`
// listener to trigger the browser's native "Leave site?" prompt; IdleLogoutGuard
// reads it to warn before the timer fires. React consumers use `subscribe`.

const dirtyKeys = new Set<string>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const cb of listeners) cb()
}

/** Record that `key` has unsaved work. Idempotent. */
export function markDirty(key: string): void {
  if (dirtyKeys.has(key)) return
  dirtyKeys.add(key)
  emit()
}

/** Clear the unsaved flag for `key`. Idempotent. */
export function clearDirty(key: string): void {
  if (!dirtyKeys.delete(key)) return
  emit()
}

/** True when any registered key still holds unsaved work. */
export function hasUnsaved(): boolean {
  return dirtyKeys.size > 0
}

/** Subscribe to dirty-state changes. Returns an unsubscribe function. */
export function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}
